import React from 'react';
/** import third-party libraries in the section above, Sort Lines Ascending */

import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
/** import mui components in the section above, Sort Lines Ascending */

import { detectImpersonation } from 'services/impersonation';
import { getEnvironmentSettings } from 'services/environment';
import { isInternalUser, isRootOrImpersonatedByRoot } from 'states/profile/business-rules';
import flags from 'containers/flags/service';
import store from 'core/store';
import UserActions from 'states/resources/users/actions';
/** import single application imports in the section above, Sort Lines Ascending */

/**
 * Feature-flag name that lets non-internal users turn new reporting on.
 * Switch visibility is leftover-on on Snowflake, current session internal org
 * (including while impersonating that org), Root or Spain-root impersonator
 * (`isRootOrImpersonatedByRoot`), or this flag. Impersonating an external org
 * without a Root impersonator still needs this flag (or leftover-on).
 */
export const ALLOW_USERS_TO_ENABLE_NEW_REPORTING_FLAG =
    'efb_456_allow_users_to_enable_new_reporting';

export const PERSIST_HINT_REPORT_EDITOR = 'reportEditor';
export const PERSIST_HINT_CAMPAIGN = 'campaign';

/** Space after the label and the hint icon. */
const SWITCH_CHROME_GAP = 1;

const SWITCH_LABEL_COPY = {
    [PERSIST_HINT_CAMPAIGN]:
        '(Opt In) Use the new reporting system for all new reports. Refer to tooltip for more info',
    [PERSIST_HINT_REPORT_EDITOR]:
        'Use the new reporting system for this report only. Refer to tooltip for more info',
};

const CAMPAIGN_HINT_COPY =
    'Turning this on updates your default preference. You will still have the option to turn it off manually when creating an individual report.';

const CAMPAIGN_IMPERSONATION_HINT_COPY =
    'Opting in applies to this session only. This does not change the default reporting preferences for you or the impersonated user.';

const REPORT_EDITOR_HINT_COPY =
    'Changing it here applies to this report only and will not update your default settings.';

const SPAIN_HINT_COPY = 'The new report source is not supported in this environment.';

/**
 * Clears the impersonation toggle so the next view uses the saved preference.
 *
 * @returns {{ type: string, payload: { enabled: null } }}
 */
export function clearUseNewReportTablePageOverride() {
    return {
        type: 'PROFILE__USE_NEW_REPORT_TABLE_PAGE_OVERRIDE',
        payload: {
            enabled: null,
        },
    };
}

/**
 * Whether to show the new-reporting switch.
 * Yes if it is already on, the current user is internal, Root is impersonating
 * anyone, or the allow-users flag is on.
 *
 * @param {boolean} checked
 * @returns {boolean}
 */
export function canShowNewReportingSwitch(checked) {
    return (
        checked === true ||
        getUseNewReportTable() === true ||
        isInternalUser() ||
        isRootOrImpersonatedByRoot() ||
        flags.isEnabled(ALLOW_USERS_TO_ENABLE_NEW_REPORTING_FLAG)
    );
}

/**
 * `getUseNewReportTable`: if the reporting datasource is not Snowflake, treat as off.
 * A temporary toggle wins until it is cleared. Otherwise read
 * profile.preferences.useNewReportTable (strict true). While impersonating, that
 * saved preference is the impersonated user's.
 * Pass optional Redux state; if omitted, uses `store.getState()`.
 *
 * @param {Object} [state]
 * @returns {boolean}
 */
export function getUseNewReportTable(state) {
    const resolvedState = state || store.getState();

    if (getEnvironmentSettings().getReportingDatasource() !== 'snowflake') {
        return false;
    }

    const pageOverride = resolvedState?.profile?.useNewReportTablePageOverride;

    if (pageOverride === true || pageOverride === false) {
        return pageOverride === true;
    }

    return resolvedState?.profile?.preferences?.useNewReportTable === true;
}

/**
 * `updateUseNewReportTablePreference`: while impersonating, skip the preference
 * save and keep the toggled value for this view. Otherwise PATCHes the current
 * user with only useNewReportTable set to `enabled === true`, then refreshes
 * profile from the updated user. The profile page saves through its own action.
 *
 * @param {boolean} enabled
 * @returns {function(Function, Function): Promise<Object|void>}
 */
export function updateUseNewReportTablePreference(enabled) {
    return async (dispatch, getState) => {
        if (detectImpersonation()) {
            // Skip the preference save. Changing tabs clears this value.
            dispatch({
                type: 'PROFILE__USE_NEW_REPORT_TABLE_PAGE_OVERRIDE',
                payload: {
                    enabled: enabled === true,
                },
            });

            return;
        }

        const userId = getState()?.profile?.userId;
        const etag = getState()?.profile?._etag;
        const payload = {
            preferences: {
                useNewReportTable: enabled === true,
            },
        };

        const updatedUser = await dispatch(UserActions.update(userId, payload, etag));

        dispatch({
            type: 'PROFILE__USER_PREFERENCES_NOTIFICATIONS_LAST_SEEN__UPDATE',
            payload: {
                user: updatedUser,
            },
        });

        return updatedUser;
    };
}

/**
 * Info-icon tooltip for the switch.
 * A non-Snowflake environment uses the Spain sentence.
 * The report editor uses the per-report sentence even while impersonating.
 * Campaign (and Account) use the session sentence while impersonating, otherwise the default-preference sentence.
 *
 * @param {'reportEditor'|'campaign'} [persistHint='campaign']
 * @param {boolean} [isSnowflake=true] Whether the reporting datasource is Snowflake
 * @returns {string}
 */
export function getNewReportingSwitchHintCopy(
    persistHint = PERSIST_HINT_CAMPAIGN,
    isSnowflake = true
) {
    if (isSnowflake !== true) {
        return SPAIN_HINT_COPY;
    }

    if (persistHint === PERSIST_HINT_REPORT_EDITOR) {
        return REPORT_EDITOR_HINT_COPY;
    }

    if (detectImpersonation()) {
        return CAMPAIGN_IMPERSONATION_HINT_COPY;
    }

    return CAMPAIGN_HINT_COPY;
}

function hintTooltipTitle(copy) {
    return (
        <Typography variant="body1" component="span">
            {copy}
        </Typography>
    );
}

const HintIconWrap = React.forwardRef(function HintIconWrap(
    { children, onClick, onMouseDown, ...props },
    ref
) {
    return (
        <Box
            ref={ref}
            component="span"
            tabIndex={0}
            sx={{ display: 'inline-flex', alignItems: 'center' }}
            {...props}
            onClick={event => {
                event.preventDefault();
                event.stopPropagation();
                onClick?.(event);
            }}
            onMouseDown={event => {
                event.stopPropagation();
                onMouseDown?.(event);
            }}
        >
            {children}
        </Box>
    );
});

function PersistHintIcon({ persistHint, isSnowflake }) {
    return (
        <Tooltip title={hintTooltipTitle(getNewReportingSwitchHintCopy(persistHint, isSnowflake))}>
            <HintIconWrap>
                <InfoOutlined color="action" fontSize="small" sx={{ pointerEvents: 'none' }} />
            </HintIconWrap>
        </Tooltip>
    );
}

/**
 * Switch for new reporting. Renders nothing when `canShowNewReportingSwitch` is false.
 * On Snowflake, the switch is enabled; leftover true can be turned off, then hide rules can take it away.
 * On non-Snowflake, the switch is disabled and unchecked. The info icon sits beside the
 * label and explains the environment.
 *
 * @param {Object} props
 * @param {boolean} props.checked
 * @param {function(boolean): void} props.onChange
 * @param {'reportEditor'|'campaign'} [props.persistHint='campaign'] Label and tooltip for this surface
 */
export const NewReportingSwitch = ({ checked, onChange, persistHint = PERSIST_HINT_CAMPAIGN }) => {
    const isChecked = checked === true;

    if (!canShowNewReportingSwitch(isChecked)) {
        return null;
    }

    const isSnowflake = getEnvironmentSettings().getReportingDatasource() === 'snowflake';
    const label = SWITCH_LABEL_COPY[persistHint] || SWITCH_LABEL_COPY[PERSIST_HINT_CAMPAIGN];

    return (
        <Box display="flex" alignItems="center" gap={SWITCH_CHROME_GAP}>
            <FormControlLabel
                sx={{
                    alignItems: 'center',
                    mr: 0,
                    '& .MuiFormControlLabel-label': { whiteSpace: 'normal' },
                }}
                control={
                    <Switch
                        color="primary"
                        checked={isSnowflake && isChecked}
                        disabled={!isSnowflake}
                        onChange={event => onChange(event.target.checked)}
                    />
                }
                label={label}
            />
            <PersistHintIcon persistHint={persistHint} isSnowflake={isSnowflake} />
        </Box>
    );
};

/**
 * Switch-only row. Extra Box props such as `mb` are spread onto the row.
 *
 * @param {Object} props
 * @param {boolean} props.checked
 * @param {function(boolean): void} props.onChange
 * @param {'reportEditor'|'campaign'} [props.persistHint='campaign'] Label and tooltip for this surface
 */
export function NewReportingSwitchRow({
    checked,
    onChange,
    persistHint = PERSIST_HINT_CAMPAIGN,
    ...boxProps
}) {
    if (!canShowNewReportingSwitch(checked)) {
        return null;
    }

    return (
        <Box display="flex" alignItems="center" gap={SWITCH_CHROME_GAP} {...boxProps}>
            <NewReportingSwitch persistHint={persistHint} checked={checked} onChange={onChange} />
        </Box>
    );
}
